import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import { MuiDialog } from "../framework/muiDialog";
import { Base } from "../../framework/base";
import { SalaryPeriodDto } from "../../models/salaryPeriod/salaryPeriod";
import { MuiList } from "../framework/muiList";
import AccordionGroup from "../../framework/muiAccordion";
import { calculateWorkTimeReportData } from "../../services/workHourSalaryService";
import { showApiError } from "../framework/formUtils";
import { useAppDispatch } from "../../framework/customStore";
import {
    clearConfirmation,
    setConfirmation,
} from "../../models/store/storeActions";
import { ConfirmationDialogType } from "../../models/store/storeTypes";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import {
    EmployeeGroupCalculation,
    SalaryPeriodCalculation,
} from "../../hooks/reporting/useWorkTimeReportData";
import { useAppOptionValueEquals } from "../../hooks/useAppOption";
import { OptionWorkTimeReportCalculationButtonEnabled } from "../../models/common/consts";
import { ReportButton } from "./ReportSection";

interface Props {
    employeeGroupCalculations: EmployeeGroupCalculation[];
    reload: () => void;
}

export const WorkTimeReportCalculationDialog = ({
    employeeGroupCalculations,
    reload,
}: Props) => {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showData, setShowData] = useState(false);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [calcButtonEnabled] = useAppOptionValueEquals(
        OptionWorkTimeReportCalculationButtonEnabled,
        true
    );

    const onClose = () => setOpen(false);

    useEffect(() => {
        if (
            open &&
            !!employeeGroupCalculations &&
            employeeGroupCalculations.length === 0
        ) {
            // Close dialog automatically if missing data is defined but empty
            onClose();
        }
    }, [open, employeeGroupCalculations]);

    const calculateData = async(
        employeeIds: string[],
        employeeGroupId: string,
        salaryPeriod: SalaryPeriodDto,
        setSavingAndReload: boolean
    ) => {
        if (setSavingAndReload) {
            setSaving(true);
        }

        try {
            await calculateWorkTimeReportData({
                employeeGroupId,
                salaryPeriodId: salaryPeriod.id,
                employeeIds,
                isProvisional: true,
                calculateFillingHours: Base.isBefore(
                    salaryPeriod.endDate,
                    new Date(),
                    "day"
                ),
            });
        } catch (error) {
            showApiError(error);
        }

        if (setSavingAndReload) {
            reload();
            setSaving(false);
        }
    };

    const confirmCalculation = (
        text: string,
        cb: () => void,
        confirmEnabled: boolean
    ) => {
        if (!confirmEnabled) {
            cb();
            return;
        }

        dispatch(
            setConfirmation(
                ConfirmationDialogType.Warning,
                t("common.warning"),
                text,
                () => {
                    dispatch(clearConfirmation());
                    cb();
                },
                () => dispatch(clearConfirmation())
            )
        );
    };

    const calculateMultipleSalaryPeriods = async(
        employeeGroupId: string,
        salaryPeriods: SalaryPeriodCalculation[],
        setSavingAndReload: boolean
    ) => {
        if (setSavingAndReload) {
            setSaving(true);
        }

        for (let i = 0; i < salaryPeriods.length; i++) {
            const sp = salaryPeriods[i];

            await calculateData(
                sp.employees.map((e) => e.id),
                employeeGroupId,
                sp.salaryPeriod,
                false
            );
        }

        if (setSavingAndReload) {
            reload();
            setSaving(false);
        }
    };

    const calculateAll = async(calculations: EmployeeGroupCalculation[]) => {
        setSaving(true);

        for (let i = 0; i < calculations.length; i++) {
            const c = calculations[i];

            await calculateMultipleSalaryPeriods(
                c.employeeGroup.id,
                c.salaryPeriods,
                false
            );
        }

        reload();
        setSaving(false);
    };

    if (!calcButtonEnabled) {
        return null;
    }

    return (
        <>
            <ReportButton
                color="success"
                onClick={() => setOpen(true)}
                disabled={
                    !employeeGroupCalculations ||
                    employeeGroupCalculations?.length === 0
                }
            >
                {t("common.updateData")}
            </ReportButton>
            <MuiDialog
                title={t("common.updateData")}
                open={open}
                onClose={onClose}
                maxWidth="sm"
                disabled={saving}
                onSave={() =>
                    confirmCalculation(
                        t("reporting.confirmCalculateAllReportData"),
                        () => void calculateAll(employeeGroupCalculations),
                        employeeGroupCalculations.flatMap(
                            (c) => c.salaryPeriods
                        ).length > 2
                    )
                }
                saveLabel={t("common.updateAll")}
            >
                <LoadingIndicator loading={saving} />
                {showData ? (
                    <AccordionGroup
                        defaultExpanded={false}
                        items={employeeGroupCalculations?.map((c) => ({
                            header: c.employeeGroup.name,
                            subheader: (
                                <UpdateButton
                                    disabled={saving}
                                    onClick={() =>
                                        confirmCalculation(
                                            t(
                                                "reporting.confirmCalculateEmployeeGroupReportData"
                                            ),
                                            () =>
                                                void calculateMultipleSalaryPeriods(
                                                    c.employeeGroup.id,
                                                    c.salaryPeriods,
                                                    true
                                                ),
                                            c.salaryPeriods.length > 2
                                        )
                                    }
                                />
                            ),
                            content: (
                                <AccordionGroup
                                    defaultExpanded={false}
                                    items={c.salaryPeriods.map((sp) => ({
                                        header: `${Base.dayjsToDateStr(
                                            sp.salaryPeriod.startDate
                                        )} - ${Base.dayjsToDateStr(
                                            sp.salaryPeriod.endDate
                                        )}`,
                                        subheader: (
                                            <UpdateButton
                                                disabled={saving}
                                                onClick={() =>
                                                    void calculateData(
                                                        sp.employees.map(
                                                            (e) => e.id
                                                        ),
                                                        c.employeeGroup.id,
                                                        sp.salaryPeriod,
                                                        true
                                                    )
                                                }
                                            />
                                        ),
                                        content: (
                                            <MuiList
                                                items={sp.employees.map(
                                                    (e) => ({
                                                        id: e.id,
                                                        text: e.fullName,
                                                        secondaryAction: (
                                                            <UpdateButton
                                                                disabled={
                                                                    saving
                                                                }
                                                                onClick={() =>
                                                                    void calculateData(
                                                                        [e.id],
                                                                        c
                                                                            .employeeGroup
                                                                            .id,
                                                                        sp.salaryPeriod,
                                                                        true
                                                                    )
                                                                }
                                                            />
                                                        ),
                                                    })
                                                )}
                                            />
                                        ),
                                    }))}
                                />
                            ),
                        }))}
                    />
                ) : (
                    <Box display="flex" justifyContent="center">
                        <Button
                            variant="text"
                            onClick={() => setShowData(true)}
                        >
                            {t("reporting.showMoreInfo")}
                        </Button>
                    </Box>
                )}
            </MuiDialog>
        </>
    );
};

interface UpdateButtonProps {
    disabled: boolean;
    onClick: () => void;
}

const UpdateButton = ({ disabled, onClick }: UpdateButtonProps) => {
    const { t } = useTranslation();

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
        e.stopPropagation();
        onClick();
    };

    return (
        <Button
            variant="contained"
            color="success"
            size="small"
            sx={{ mr: 1 }}
            disabled={disabled}
            onClick={handleClick}
        >
            {t("common.update")}
        </Button>
    );
};
